import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pesnya-na-yubiley")!;

const faqItems = [
  { q: "На какой юбилей можно заказать песню?", a: "На любой: 30, 40, 50, 60 лет — и даже на юбилей свадьбы или карьеры." },
  { q: "Что рассказать, чтобы песня получилась личной?", a: "Расскажите главное: кем работает юбиляр, какие у него привычки, что он любит, какие смешные истории вы помните." },
  { q: "Можно ли включить песню при гостях на банкете?", a: "Конечно. Файл скачивается в высоком качестве — можно передать ведущему или включить через колонку." },
  { q: "Сколько стоит песня на юбилей?", a: "Демоверсия бесплатна. Полная версия — от 249 ₽." },
  { q: "Успею ли я, если юбилей уже завтра?", a: "Да. Весь процесс занимает несколько минут." },
];

const PesnyaNaYubiley = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">Песня на юбилей на заказ</h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Пятьдесят лет. Полвека историй, решений, ошибок и побед. Что ему подарить?</p>
            <p>Ответ — <strong className="text-foreground">песню, которая расскажет его жизнь</strong>. Не стандартные пожелания, а настоящую историю.</p>
            <p><strong className="text-foreground">Персональная песня на юбилей</strong> — это подарок, который невозможно купить в магазине.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Кому дарят песню на юбилей</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground">Папе или <Link to="/pesnya-mame-v-podarok" className="text-primary hover:underline">маме</Link>.</strong> Когда юбиляр слышит свою жизнь в куплетах — это сильнее любого тоста.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-pro-druga" className="text-primary hover:underline">Другу</Link>.</strong> Песня с общими историями — мощнее любого подарка.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-kollege-v-podarok" className="text-primary hover:underline">Коллеге</Link>.</strong> Трек с именем юбиляра и шутками про планёрки запоминается всем отделом.</p>
              <p><strong className="text-foreground">Себе.</strong> Люди заказывают песни на свой юбилей, чтобы удивить гостей.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎂 Создать песню на юбилей</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Готово за 3 минуты.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Как создать юбилейную песню за 3 минуты</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Расскажите про юбиляра. Выберите стиль. Через несколько минут — готовая демоверсия. Можно создать красивую подарочную страницу и отправить ссылку.</p>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«Папа слушал молча. А потом сказал: вот теперь я чувствую, что прожил жизнь не зря»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Дочь заказала песню папе на шестидесятилетие. На банкете включили между тостами. Папа поднял глаза и обнял дочь так крепко, как не обнимал с её детства.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Подарить юбиляру песню, которую он не забудет</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Частые вопросы о песнях на юбилей</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Пусть юбилей зазвучит по-настоящему</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Создайте песню, которая расскажет историю целой жизни.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PesnyaNaYubiley;
